import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  type ActionHandler,
  Create,
  type Nullable,
  type ObjectSchemaDefinition,
  SingleLineTextField,
} from '@axinom/mosaic-ui';
import { config } from 'config';
import { Field } from 'formik';
import {
  type CreateServiceDefinitionMutation,
  type CreateServiceDefinitionMutationVariables,
  useCreateServiceDefinitionMutation,
} from 'generated/hosting-service';
import type { BreadcrumbResolver } from 'models/breadcrumb';
import { environmentApolloClient } from 'modules/environmentApolloClient';
import React, { useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';

const createServiceValidationSchema = Yup.object<ObjectSchemaDefinition>({
  name: Yup.string().required('Service Name is required'),
  serviceId: Yup.string()
    .required('Service ID is required')
    .matches(
      /^([a-z\\-]+)$/,
      'The Service ID must contain only lowercase alpha characters and dashes.',
    ),
});

type FormData = Nullable<
  CreateServiceDefinitionMutationVariables['input']['serviceDefinition']
>;

type SubmitResponse = Nullable<
  CreateServiceDefinitionMutation['createServiceDefinition']
>;

export const CustomizableServiceCreate: React.FC = () => {
  const { envId } = useParams<{
    envId: string;
  }>();

  const apolloClient = environmentApolloClient(
    envId,
    new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
  );

  const [createServiceDefinition] = useCreateServiceDefinitionMutation({
    client: apolloClient,
    fetchPolicy: 'no-cache',
  });

  const saveData = useCallback(
    async (formData: FormData): Promise<SubmitResponse> => {
      const response = await createServiceDefinition({
        variables: {
          input: {
            serviceDefinition: {
              serviceId: formData?.serviceId ?? '',
              name: formData?.name ?? '',
              repositoryName: '',
            },
          },
        },
      });
      return response.data?.createServiceDefinition ?? null;
    },
    [createServiceDefinition],
  );

  const history = useHistory();
  const onProceed = useCallback<ActionHandler<FormData, SubmitResponse>>(
    ({ submitResponse }) => {
      if (submitResponse?.serviceDefinition?.name) {
        history.push(
          `/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services/${submitResponse.serviceDefinition.id}`,
        );
      } else {
        // The schema has the response.data properties marked as optional, since theoretically a user could have
        // permissions to mutate but not to read. In practice this can not happen on that service, so we just throw
        // an error in case we get there.
        throw new Error('Not expected');
      }
    },
    [envId, history],
  );

  return (
    <Create<FormData, SubmitResponse>
      title="New Service"
      validationSchema={createServiceValidationSchema}
      saveData={saveData}
      onProceed={onProceed}
      cancelNavigationUrl={`/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services`}
      initialData={{
        loading: false,
      }}
    >
      <Field name="name" label="Service Name" as={SingleLineTextField} />
      <Field
        name="serviceId"
        label="Service ID"
        tooltipContent={`Unique string used to refer to the service containing only lowercase alpha and dash characters. Once the service is created, the service ID cannot be changed.`}
        as={SingleLineTextField}
      />
    </Create>
  );
};
export const CustomizableServicesCreateCrumb: BreadcrumbResolver = () =>
  'New Service';
